import React, { useState, useCallback, useEffect, useMemo, Component } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { getInvoicePdf } from "@/data";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

// PDF.js'in bilinen 'sendWithStream' hatasını global olarak bastırıyoruz.
// Bu hata, parent bileşen re-render olduğunda PDF.js worker'ının eski transport'u
// kullanmaya çalışmasından kaynaklanır. Görsel bir etkisi yoktur, sadece konsol hatası verir.
// Bu handler olmadan hata React'a ulaşır ve beyaz ekrana neden olur.
if (typeof window !== 'undefined') {
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (error && error.message && error.message.includes('sendWithStream')) {
      console.warn('PDF.js sendWithStream error suppressed');
      return true; // Hatayı yut, React'a iletme
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };
}

// PDF.js worker hataları (sendWithStream vb.) React'ı çökertmesin diye
// Error Boundary ile sarmalıyoruz
class PDFErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn("PDFErrorBoundary caught:", error?.message);
  }

  componentDidUpdate(prevProps) {
    // fileSource değiştiğinde hatayı sıfırla, yeni PDF yüklenebilsin
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#f59e0b",
            fontSize: "14px",
            padding: "20px",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <span>PDF görüntülemede bir sorun oluştu.</span>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{
              padding: "6px 16px",
              borderRadius: "6px",
              border: "1px solid #f59e0b",
              background: "transparent",
              color: "#f59e0b",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Tekrar Dene
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PDFViewerCustom = ({ url, commissionDetailId, height = "800px" }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);

  // Backend proxy üzerinden PDF'i fetch et
  useEffect(() => {
    let currentBlobUrl = null;
    
    if (commissionDetailId) {
      setLoading(true);
      setError(null);
      
      getInvoicePdf(commissionDetailId)
        .then((res) => {
          // Önceki URL varsa temizleyebiliriz ama Document yüklenirken hata verebilir
          // Bu yüzden sadece yenisini oluşturup set ediyoruz
          const newBlobUrl = URL.createObjectURL(res.data);
          currentBlobUrl = newBlobUrl;
          setPdfData(newBlobUrl);
        })
        .catch((err) => {
          console.error("PDF proxy error:", err);
          setError("PDF yüklenirken bir hata oluştu.");
          setLoading(false);
        });
    }

    // Sadece bileşen tamamen unmount olduğunda veya ID değiştiğinde 
    // ama bir gecikmeyle temizlik yapıyoruz ki 'ERR_FILE_NOT_FOUND' olmasın
    return () => {
      if (currentBlobUrl) {
        // Tarayıcıya dosyayı okuması için 1 saniye süre verip sonra belleği temizliyoruz
        setTimeout(() => URL.revokeObjectURL(currentBlobUrl), 1000);
      }
    };
  }, [commissionDetailId]);

  // Kullanılacak dosya kaynağı: proxy varsa pdfData, yoksa doğrudan url
  const fileSource = commissionDetailId ? pdfData : url;

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    console.error("PDF load error:", err);
    setError("PDF yüklenirken bir hata oluştu.");
    setLoading(false);
  }, []);

  const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () =>
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.4));
  const resetZoom = () => setScale(1.0);
  const rotate = () => setRotation((prev) => (prev + 90) % 360);

  // pdf.js'in fontları ve karakter haritalarını (CMaps) bulabilmesi için gerekli URL'ler
  // useMemo ile sabitliyoruz ki her render'da yeni obje oluşmasın ve react-pdf dokümanı yeniden yüklemesin
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  // Proxy modunda pdfData henüz yüklenmediyse loading göster
  if (commissionDetailId && !pdfData && !error) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          color: "#64748b",
        }}
      >
        PDF yükleniyor...
      </div>
    );
  }

  return (
    <div
      style={{
        height,
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          backgroundColor: "#1e293b",
          color: "white",
          fontSize: "13px",
          flexShrink: 0,
        }}
      >
        {/* Sayfa navigasyonu */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            type="button"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: pageNumber <= 1 ? "#64748b" : "white",
              cursor: pageNumber <= 1 ? "not-allowed" : "pointer",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "13px",
            }}
          >
            ◀
          </button>
          <span>
            {pageNumber} / {numPages || "—"}
          </span>
          <button
            type="button"
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: pageNumber >= (numPages || 1) ? "#64748b" : "white",
              cursor: pageNumber >= (numPages || 1) ? "not-allowed" : "pointer",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "13px",
            }}
          >
            ▶
          </button>
        </div>

        {/* Zoom & Rotate kontrolleri */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            type="button"
            onClick={zoomOut}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              cursor: "pointer",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "13px",
            }}
          >
            −
          </button>
          <button
            type="button"
            onClick={resetZoom}
            style={{
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
              fontSize: "13px",
              minWidth: "50px",
            }}
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              cursor: "pointer",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "13px",
            }}
          >
            +
          </button>
          <button
            type="button"
            onClick={rotate}
            title="Döndür"
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              cursor: "pointer",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "13px",
              marginLeft: "4px",
            }}
          >
            ↻
          </button>
        </div>
      </div>

      {/* PDF İçerik alanı */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#e2e8f0",
        }}
      >
        {error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#ef4444",
              fontSize: "14px",
              padding: "20px",
            }}
          >
            {error}
          </div>
        ) : (
          <PDFErrorBoundary resetKey={fileSource}>
            <Document
              key={fileSource}
              file={fileSource}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              options={pdfOptions}
              loading={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    padding: "40px",
                    color: "#64748b",
                  }}
                >
                  PDF yükleniyor...
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                rotate={rotation}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                onRenderError={(error) => {
                  console.warn("PDF page render error (ignored):", error?.message);
                }}
                loading={
                  <div
                    style={{
                      padding: "40px",
                      color: "#64748b",
                      textAlign: "center",
                    }}
                  >
                    Sayfa yükleniyor...
                  </div>
                }
              />
            </Document>
          </PDFErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default React.memo(PDFViewerCustom);
